import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { i18nValue } from "../../functions";
import { UserContext } from "../../context/user";
import useFoodMenu from "../../hooks/useFoodMenu";
import {
  buildMenuCart,
  buildMenuItemsMap,
  buildMissingMenuCustomizations,
  buildMenuRequestPayload,
  createEmptyMenuCart,
  filterMenuItemsBySearch,
  normalizeMenuCustomizations,
  removeMenuCustomizationUnit,
} from "./menuCommerceAdapters";

const useMenuCommerce = ({
  menuId,
  requestType = "room_service",
  language,
  askForDeliveryPoint = true,
  deliveryPointEditable = true,
  defaultDeliveryPointCode = "",
}) => {
  const { getFoodMenu } = useFoodMenu();
  const { requestCreate } = useContext(UserContext);

  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quantities, setQuantities] = useState({});
  const [cart, setCart] = useState(createEmptyMenuCart());
  const [cartItemsCustomizations, setCartItemsCustomizations] = useState({});
  const [summary, setSummary] = useState(false);
  const [observations, setObservations] = useState("");
  const [deliveryPoint, setDeliveryPoint] = useState("");
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  useEffect(() => {
    setDeliveryPoint("");
  }, [defaultDeliveryPointCode, askForDeliveryPoint, deliveryPointEditable]);

  useEffect(() => {
    if (!menuId) {
      setMenu(null);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    getFoodMenu(undefined, menuId)
      .then((response) => {
        if (cancelled) {
          return;
        }

        if (
          !response ||
          !Array.isArray(response.content) ||
          response.content.length === 0 ||
          response.content.every((section) => !Array.isArray(section?.items) || section.items.length === 0)
        ) {
          setMenu(null);
          setError("empty");
          return;
        }

        setMenu(response);
      })
      .catch(() => {
        if (!cancelled) {
          setMenu(null);
          setError("load");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [menuId]);

  const itemsMap = useMemo(() => buildMenuItemsMap(menu), [menu]);

  useEffect(() => {
    setCart(
      buildMenuCart({
        quantities,
        itemsMap,
        language,
        i18nValue,
        customizations: cartItemsCustomizations,
      })
    );
  }, [cartItemsCustomizations, itemsMap, language, quantities]);

  const missingCustomizations = useMemo(
    () =>
      buildMissingMenuCustomizations({
        cartItems: cart?.items || [],
        customizations: cartItemsCustomizations,
      }),
    [cart?.items, cartItemsCustomizations]
  );

  const hasCustomizableItems = useMemo(
    () => (cart?.items || []).some((item) => Object.keys(item?.preferences || {}).length > 0),
    [cart?.items]
  );

  const hasMissingCustomizations = useMemo(
    () => Object.keys(missingCustomizations).length > 0,
    [missingCustomizations]
  );

  const resolvedDeliveryPoint = useMemo(() => {
    if (!askForDeliveryPoint) {
      return defaultDeliveryPointCode || "";
    }

    if (!deliveryPointEditable) {
      return defaultDeliveryPointCode || "";
    }

    return `${defaultDeliveryPointCode || ""}${String(deliveryPoint || "").trim()}`;
  }, [askForDeliveryPoint, defaultDeliveryPointCode, deliveryPoint, deliveryPointEditable]);

  const isDeliveryPointMissing = useMemo(() => {
    if (!askForDeliveryPoint || !deliveryPointEditable) {
      return false;
    }

    return !String(deliveryPoint || "").trim();
  }, [askForDeliveryPoint, deliveryPoint, deliveryPointEditable]);

  const addItem = useCallback((itemId) => {
    setQuantities((current) => ({
      ...current,
      [itemId]: Number(current[itemId] || 0) + 1,
    }));
  }, []);

  const removeItem = useCallback((itemId, unitIndex = null) => {
    const fallbackRemoveIndex = Math.max(Number(quantities[itemId] || 1) - 1, 0);
    const targetUnitIndex = typeof unitIndex === "number" ? unitIndex : fallbackRemoveIndex;

    setQuantities((current) => {
      const nextQty = Math.max(Number(current[itemId] || 0) - 1, 0);
      const next = {
        ...current,
        [itemId]: nextQty,
      };

      if (!nextQty) {
        delete next[itemId];
      }

      return next;
    });

    setCartItemsCustomizations((current) => {
      if (!current?.[itemId]) {
        return current;
      }

      const nextItemCustomizations = removeMenuCustomizationUnit(current[itemId], targetUnitIndex);
      const next = { ...current };

      if (!Object.keys(nextItemCustomizations).length) {
        delete next[itemId];
        return next;
      }

      next[itemId] = nextItemCustomizations;
      return next;
    });
  }, [quantities]);

  const updateCustomization = useCallback((itemId, unitIndex, preferenceId, optionId) => {
    setCartItemsCustomizations((current) => {
      const normalizedItemCustomizations = normalizeMenuCustomizations(current?.[itemId] || {});
      const nextUnitCustomizations = {
        ...(normalizedItemCustomizations[unitIndex] || {}),
      };

      if (optionId) {
        nextUnitCustomizations[preferenceId] = optionId;
      } else {
        delete nextUnitCustomizations[preferenceId];
      }

      return {
        ...current,
        [itemId]: normalizeMenuCustomizations({
          ...normalizedItemCustomizations,
          [unitIndex]: nextUnitCustomizations,
        }),
      };
    });
  }, []);

  const resetCart = useCallback(() => {
    setQuantities({});
    setCartItemsCustomizations({});
    setSummary(false);
    setObservations("");
    setDeliveryPoint("");
  }, []);

  const openSummary = useCallback(() => {
    if (cart?.qty) {
      setSummary(true);
    }
  }, [cart?.qty]);

  const closeSummary = useCallback(() => {
    setSummary(false);
  }, []);

  const submitRequest = useCallback(async () => {
    if (!cart?.qty) {
      return null;
    }

    if (hasMissingCustomizations) {
      setSummary(true);
      return null;
    }

    if (isDeliveryPointMissing) {
      setSummary(true);
      return null;
    }

    const payload = buildMenuRequestPayload({
      requestType,
      quantities,
      cart,
      deliveryPoint: resolvedDeliveryPoint,
      observations,
    });

    setSending(true);
    try {
      const response = await requestCreate(payload);
      return response;
    } finally {
      setSending(false);
    }
  }, [
    cart,
    hasMissingCustomizations,
    isDeliveryPointMissing,
    observations,
    quantities,
    requestCreate,
    requestType,
    resetCart,
    resolvedDeliveryPoint,
  ]);

  const filterItems = useCallback(
    (items) =>
      filterMenuItemsBySearch({
        items,
        searchTerm,
        language,
        i18nValue,
      }),
    [language, searchTerm]
  );

  return {
    menu,
    loading,
    error,
    quantities,
    cart,
    summary,
    setSummary,
    observations,
    setObservations,
    deliveryPoint,
    setDeliveryPoint,
    resolvedDeliveryPoint,
    isDeliveryPointMissing,
    sending,
    searchTerm,
    setSearchTerm,
    activeCategoryIndex,
    setActiveCategoryIndex,
    cartItemsCustomizations,
    missingCustomizations,
    hasCustomizableItems,
    hasMissingCustomizations,
    addItem,
    removeItem,
    updateCustomization,
    resetCart,
    openSummary,
    closeSummary,
    submitRequest,
    filterItems,
  };
};

export default useMenuCommerce;
